import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { object, string, array } from 'yup';
import { useFormik } from 'formik';

// Types
import { ButtonType } from '../../types/button-type';
import { ElementSize } from '../../types/element-size';

import ConfigurationCycle, { ConfigurationCycleOption } from '../../components/ConfigurationCycle/ConfigurationCycle';
import ControllerTabs from '../../components/ControllerTabs/ControllerTabs';
import AddControllerModal from '../../components/AddControllerModal/AddControllerModal';
import InputControl from '../../components/InputControl/InputControl';
import InteractiveButton from '../../components/InteractiveButton/InteractiveButton';
import CycleSettingsInput from '../../components/CycleSettingsInput/CycleSettingsInput';

import { Dispatch, RootState } from '../../store/store';
import { connect } from 'react-redux';
import SelectOption, { Option, SelectedValue } from '../../components/SelectOption/SelectOption';
import { TEXT_LENGTH_LIMITS } from '../../constants/common';
import { ItemList } from '../../types/cycle';
import { CHANNELS, THERMOCOUPLES } from '../../constants/cycle';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import ROUTE from '../../constants/route-constants';
import { Location } from '../../store/models/locations';
import { Contract } from '../../store/models/contracts';
import { Controller } from '../../store/models/controllers';
import SpinnerLoader from '../../components/SpinnerLoader/SpinnerLoader';
import CycleSettings from '../../enums/cycle-setting.enum';
import ComponentNameCycleList from '../../enums/component-name-cycle-list.enum';
import CycleStatus from '../../enums/cycle-status.enum';
import SearchCycle from '../../components/SearchCycle/SearchCycle';
import { SearchCycle as SearchCycleQuery } from '../../types/search-name';
import { Cycle } from '../../store/models/cycles';
import { IChannelName } from '../../types/channel-types';
import { capitalizeFirstLetter, getChannelNames } from './utils';
import CycleConflictBanner from '../../components/CycleConflictBanner/CycleConflictBanner';
import { CycleConflictError, getCycleConflictError } from '../../utils/cycle-conflict-error';
import CycleMeasurement from '../../enums/cycle-measurement';
import CycleMeasurementSymbol from '../../enums/cycle-measurement-symbol';
import { getMeasurementFromUnitSource } from '../../components/CycleSettings/components/UnitMismatchWarning';
import { createCycleSettingsValidationSchema, convertCycleSettingsUnits, applyDefaultTolerances } from '../../utils/cycleSettingsForm';
import { convertAbsoluteTemperature, convertTemperatureDelta } from '../../utils/temperature-unit-conversion';

type FormProps = {
  name: string | null;
  list: ItemList[];
  locationId: string | null;
  contractId: string | null;
  cycleSettings: {
    name?: string | null;
    mode: string | null;
    selectedRecipeId?: string | null;
    rate?: number | string;
    finalTemp?: number | string;
    soakUpper?: number | string;
    soakLower?: number | string;
    percentage_timer?: number | string;
    rateUpperTolerance?: number | string;
    rateLowerTolerance?: number | string;
    holdUpperTolerance?: number | string;
    holdLowerTolerance?: number | string;
    steps?: any[];
  };
};

const ROW_BLOCK_CLASS = 'grid grid-cols-[130px_1fr] items-center gap-x-3';
const ROW_LABEL_CLASS = 'text-sm 2xl:text-base font-medium text-black dark:text-white';

type ComponentProps = {
  user: any;
  controllers: Controller[];
  locations: Location[];
  contracts: Contract[];
  getControllersByOrganizationId: (id: any) => any;
  createCycle: (payload: any) => any;
  duplicateCycle: (data: { id: string; count: number }) => Promise<Cycle[]>;
  getLocations: () => any;
  getContractsByLocationId: (payload: any) => any;
  getContractById: (id: any) => any;
  getCyclesByContractId: (payload: any) => any;
  cyclesBySearch: any[];
  getChannels: (id: any) => any;
  clearChannels: () => any;
  getCyclesByLocationId: (payload: any) => any;
  getRecipe: (id: string) => any;
  getRecipeSteps: (id: string) => any;
};

const createCycleValidationSchema = (measurement: CycleMeasurement) =>
  object({
    name: string()
      .required('Required')
      .min(TEXT_LENGTH_LIMITS.MIN, `Min first name length is ${TEXT_LENGTH_LIMITS.MIN} characters`)
      .max(TEXT_LENGTH_LIMITS.MAX, `Max first name length is ${TEXT_LENGTH_LIMITS.MAX} characters`)
      .matches(/^[a-zA-Z0-9 _-]+$/, 'Name can only contain letters, numbers, spaces, underscores, and hyphens')
      .test('no-trim-spaces', 'Name cannot start or end with spaces', (value) =>
        value ? value.trim() === value : true,
      ),
    list: array()
      .min(1, 'Must contain at least one controller')
      .test(
        'No channel or thermocouple selected',
        'Each controller must have at least one channel or thermocouple',
        (value) => {
          return value?.every((item) => item.thermocouples.length > 0 || item.channels.length > 0);
        },
      ),
    locationId: string().required('Required'),
    contractId: string().required('Required'),
    cycleSettings: createCycleSettingsValidationSchema(measurement),
  });

const thermocoupleOptions: { name: string; value: string }[] = Object.keys(THERMOCOUPLES).map((item) => ({
  name: item,
  value: item,
}));

const AddCycle: React.FC<ComponentProps> = ({
  user,
  controllers,
  locations,
  contracts,
  getControllersByOrganizationId,
  createCycle,
  duplicateCycle,
  getLocations,
  getContractsByLocationId,
  getContractById,
  getCyclesByContractId,
  cyclesBySearch,
  getChannels,
  clearChannels,
  getCyclesByLocationId,
  getRecipe,
  getRecipeSteps,
}) => {
  const navigate = useNavigate();
  const { clientId, contractId } = useParams();

  const [editedController, setEditedController] = useState<Controller | null>(null);
  const [isAddControllerModalOpen, setIsAddControllerModalOpen] = useState<boolean>(false);
  const [isDuplicate, setIsDuplicate] = useState<boolean>(false);
  const [channelsByController, setChannelsByController] = useState<Record<string, IChannelName[]>>({});
  const [loadingChannels, setLoadingChannels] = useState<boolean>(false);
  const [cycleConflict, setCycleConflict] = useState<CycleConflictError | null>(null);
  const [cycleSettingsMeasurement, setCycleSettingsMeasurement] = useState(CycleMeasurement.Celsius);
  const previousCycleSettingsMeasurement = useRef(CycleMeasurement.Celsius);
  const cycleValidationSchema = useMemo(
    () => createCycleValidationSchema(cycleSettingsMeasurement),
    [cycleSettingsMeasurement],
  );

  const formik = useFormik<FormProps>({
    initialValues: {
      name: null,
      list: [],
      locationId: clientId,
      contractId: contractId ?? null,
      cycleSettings: {
        mode: null,
      },
    },
    validationSchema: cycleValidationSchema,
    onSubmit: async (values) => {
      const { cycleSettings, ...cyclePayload } = values;
      // cycleSettings is held (and displayed/edited) in cycleSettingsMeasurement
      // (the contract's unit, e.g. Fahrenheit), but CycleSettingsDto has no unit
      // awareness at all - it validates and stores every value as Celsius-native.
      // Without this conversion, a Fahrenheit-displayed value (or a recipe's
      // values, which go through this same display conversion when applied -
      // see onRecipeSelect below) gets sent raw and compared against a
      // Celsius-scaled max, rejecting anything large enough to exceed it after
      // the implicit 1.8x unit gap - which silently blocked "Create" entirely.
      const celsiusCycleSettings = cycleSettings
        ? (convertCycleSettingsUnits(cycleSettings, cycleSettingsMeasurement, CycleMeasurement.Celsius) as FormProps['cycleSettings'])
        : cycleSettings;
      // The form keeps this field as percentage_timer (matches
      // CycleSettingsInput/the validation schema), but CreateCycleDto's
      // cycleSettings.percentageTimer is camelCase - left untranslated here,
      // the backend DTO never sees the typed value, silently storing
      // percentageTimer as undefined and breaking "Apply to All" later.
      const normalizedSettings = celsiusCycleSettings?.mode ? applyDefaultTolerances(celsiusCycleSettings) : null;
      const pendingCycleSettings = normalizedSettings
        ? (() => {
          const { percentage_timer: percentageTimer, ...rest } = normalizedSettings;
          return { ...rest, percentageTimer };
        })()
        : null;

      const payload = pendingCycleSettings ? { ...cyclePayload, cycleSettings: pendingCycleSettings } : cyclePayload;

      try {
        setCycleConflict(null);
        await createCycle(payload);
      } catch (error) {
        const conflict = getCycleConflictError(error);

        if (conflict) {
          setCycleConflict(conflict);
          return;
        }

        throw error;
      }

      let path;

      if (contractId) {
        path = generatePath(ROUTE.LOCATION_CONTRACT_SCREENS, { clientId, contractId: values.contractId });
      } else {
        path = generatePath(ROUTE.LOCATION_CONTRACTS_OVERVIEW, { clientId });
      }

      const searchParams = new URLSearchParams({ status: CycleStatus.InActive }).toString();

      navigate(`${path}?${searchParams}`);
    },
  });
  const isDataLoaded = useMemo(() => contracts !== null && locations !== null, [contracts, locations]);

  const disabledContract = formik.values.locationId === null || locations === null || locations.length === 0;
  const disabledScreen = disabledContract || formik.values.contractId === null;
  const disabledController = disabledScreen;
  const disableChannelsThermocouples = !editedController || formik.values.list.length === 0;
  // Not gated on formik.isValid - with validateOnChange, any out-of-range
  // recipe value makes the form invalid (and this button disabled) the
  // moment it's typed, before the field is ever touched, so clicking
  // "Create" was a silent no-op with no visible error. Leaving the button
  // enabled lets the click reach formik.handleSubmit, which marks every
  // field touched and surfaces the validation errors instead of swallowing
  // the attempt.
  const disabledSubmit = !formik.dirty || formik.isSubmitting;

  const selectedContract = useMemo(() => {
    return contracts?.find((contract) => contract.id === formik.values.contractId) || null;
  }, [contracts, formik.values.contractId]);

  const selectedContractMeasurement =
    selectedContract?.isCelsius === false ? CycleMeasurement.Fahrenheit : CycleMeasurement.Celsius;

  const hasMixedContractControllerUnits = useMemo(() => {
    return formik.values.list.some((listItem) => {
      const controller = controllers.find((item) => item.serial_number === listItem.serial_number);
      const selectedChannelUnits = channelsByController[listItem.serial_number]?.find((channel) =>
        listItem.channels.includes(channel.channel),
      )?.units;
      const controllerMeasurement = getMeasurementFromUnitSource(
        controller?.units ?? selectedChannelUnits,
        (controller as { isCelsius?: boolean })?.isCelsius,
      );

      return controllerMeasurement !== null && controllerMeasurement !== selectedContractMeasurement;
    });
  }, [channelsByController, controllers, formik.values.list, selectedContractMeasurement]);

  useEffect(() => {
    const previousMeasurement = previousCycleSettingsMeasurement.current;

    if (previousMeasurement === selectedContractMeasurement) {
      setCycleSettingsMeasurement(selectedContractMeasurement);
      return;
    }

    const convertedSettings = convertCycleSettingsUnits(
      formik.values.cycleSettings,
      previousMeasurement,
      selectedContractMeasurement,
    );

    previousCycleSettingsMeasurement.current = selectedContractMeasurement;
    setCycleSettingsMeasurement(selectedContractMeasurement);
    formik.setFieldValue('cycleSettings', convertedSettings, false);
    setTimeout(() => formik.validateField('cycleSettings'), 0);
  }, [selectedContractMeasurement]);

  const channelOptions = useMemo<{ name: string; value: string }[]>(() => {
    if (editedController && editedController.serial_number in channelsByController) {
      const channels = channelsByController[editedController.serial_number];
      return channels.map((channel) => ({
        name: channel.name?.trim() || channel.channel,
        value: channel.channel,
      }));
    }

    return Object.keys(CHANNELS).map((key) => ({
      name: key,
      value: key,
    }));
  }, [editedController, channelsByController]);

  const getCycles = (payload: { query?: SearchCycleQuery }) => {
    if (contractId) {
      return getCyclesByContractId({ contractId, ...payload });
    }

    return getCyclesByLocationId({ locationId: clientId, ...payload });
  };

  const fetchContractsByLocationId = async (locationId: string) => {
    await getContractsByLocationId({ locationId });
  };

  const fetchControllersByOrganizationId = async (organizationId) => {
    try {
      await getControllersByOrganizationId(organizationId);
    } catch (error) {
      console.error(error);
    }
  };

  const openModal = () => {
    setIsDuplicate(true);
  };

  const closeModal = () => {
    setIsDuplicate(false);
  };

  // Creates a full server-side duplicate of the picked cycle (controllers,
  // recipe, meta data, PDF attachments - everything except the name) via the
  // same endpoint used elsewhere in the app, then takes the user to the
  // Inactive tab (where every duplicate lands - see duplicateCycle in
  // cycles.service.ts) for this same client/contract, matching where a
  // regular "Create" lands them.
  const duplicateData = async (cycle: Cycle) => {
    closeModal();

    try {
      await duplicateCycle({ id: cycle.id, count: 1 });
    } catch (error) {
      // duplicateCycle's store effect already toasts the error - nothing
      // else to do here, just don't fall through to navigating as if it
      // had succeeded.
      return;
    }

    let path;

    if (contractId) {
      path = generatePath(ROUTE.LOCATION_CONTRACT_SCREENS, { clientId, contractId });
    } else {
      path = generatePath(ROUTE.LOCATION_CONTRACTS_OVERVIEW, { clientId });
    }

    const searchParams = new URLSearchParams({ status: CycleStatus.InActive }).toString();

    navigate(`${path}?${searchParams}`);
  };

  const getLocationsOptions = useCallback(
    (locations: Location[]) => {
      if (locations.length === 0) {
        return [];
      }

      const options: Option[] = generateOptions(locations);
      return options;
    },
    [locations],
  );

  const getContractOptions = useCallback(
    (locations: Location[], contracts: Contract[]) => {
      if (locations.length === 0 || contracts.length === 0) {
        return [];
      }

      const options: Option[] = generateOptions(contracts);
      return options;
    },
    [locations, contracts],
  );


  const generateOptions = (listData: Location[] | Contract[]): Option[] => {
    return listData.map((data) => {
      const option: Option = { id: data.id, value: data.name };

      return option;
    });
  };

  const getSelectedOption = (listData: Location[] | Contract[], id: string | undefined): Option | null => {
    if (listData.length === 0 || !id) {
      return null;
    }

    const data = listData.find((option) => option.id === id);

    return { id, value: data.name };
  };

  const selectOption = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = event.target;

    const selectedValue: SelectedValue = value as unknown as SelectedValue;

    if (name === 'locationId') {
      formik.setFieldValue(name, selectedValue.id);
      formik.setFieldValue('contractId', null);
      formik.setFieldValue('screenId', null);
      formik.setFieldValue('list', []);
      formik.setTouched({ [name]: true });

      setEditedController(null);
      clearChannels();

      await fetchContractsByLocationId(selectedValue.id);
      return;
    }

    if (name === 'contractId') {
      formik.setFieldValue(name, selectedValue.id);
      formik.setFieldValue('screenId', null);
      formik.setTouched({ [name]: true });

      return;
    }

    if (name === 'cycleSettings.mode') {
      // Handle clearing cycle settings
      if (selectedValue.id === '') {
        formik.setFieldValue('cycleSettings', { mode: null });
      } else {
        formik.setFieldValue(name, selectedValue.id);
      }
      formik.setFieldTouched(name, true);
      return;
    }

    formik.setFieldValue(name, selectedValue.id);
    formik.setTouched({ [name]: true });
    return;
  };

  const resetData = async () => {
    await fetchContractsByLocationId(clientId);
    formik.resetForm();
    setEditedController(null);
    clearChannels();
  };

  const handleSelect = (cycleEntityName: ComponentNameCycleList, event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;

    let list = formik.values.list;

    if (!formik.touched?.list) {
      formik.setFieldTouched('list', true);
    }

    if (cycleEntityName === ComponentNameCycleList.Controllers) {
      const controller = controllers.find((controller) => controller.serial_number === value);

      if (checked) {
        list.push({
          id: controller.id,
          name: controller.name,
          serial_number: controller.serial_number,
          is_active: controller.is_active,
          thermocouples: [],
          channels: [],
        });
      }

      if (!checked) {
        list = list.filter((item) => item.serial_number !== value);
      }

      if (!checked && editedController?.serial_number === value) {
        setEditedController(null);
        clearChannels();
      }

      if (checked && editedController === null) {
        const controller = controllers.find((controller) => controller.serial_number === value);
        setEditedController(controller);
      }
      formik.setFieldValue('list', list);

      return;
    }

    if (
      cycleEntityName === ComponentNameCycleList.Channels ||
      cycleEntityName === ComponentNameCycleList.Thermocouples
    ) {
      const keyField = cycleEntityName.toLocaleLowerCase();
      const updatedList = list.map((item) => {
        if (item.serial_number === editedController?.serial_number && checked) {
          item[keyField] = [...item[keyField], value];
          return item;
        }

        if (item.serial_number === editedController?.serial_number && !checked) {
          item[keyField] = item[keyField].filter((item) => item !== value);
          return item;
        }

        return item;
      });

      formik.setFieldValue('list', updatedList);

      return;
    }
  };

  const convertListToView = (list: ItemList[]) => {
    const selectionReport = list.map((item) => {
      const listChannels = getChannelNames(item.channels, channelsByController, item.serial_number);

      return `${item.name} | ${item.serial_number}: ${[...listChannels, ...item.thermocouples].join(',')}`;
    });

    return selectionReport;
  };

  const getTitleByEntity = (cycleEntityName: ComponentNameCycleList) => {
    const name = capitalizeFirstLetter(cycleEntityName);

    if (cycleEntityName === ComponentNameCycleList.Controllers) {
      return name;
    } else {
      const controller = editedController
        ? controllers.find((item) => item.serial_number === editedController?.serial_number)
        : null;
      const titleController = controller ? `for ${controller.name}` : '';
      return `${name} ${titleController}`;
    }
  };

  const getEntityValue = (name: ComponentNameCycleList) => {
    if (!formik.values.list || formik.values.list.length === 0) {
      return null;
    }

    if (name === ComponentNameCycleList.Controllers) {
      return formik.values.list.map((item) => item.serial_number);
    }

    if (name === ComponentNameCycleList.Channels || name === ComponentNameCycleList.Thermocouples) {
      const keyField = name.toLocaleLowerCase();
      const values = formik.values.list.find((item) => item.serial_number === editedController?.serial_number);

      return values ? values[keyField] : null;
    }

    return null;
  };

  const changeEditingController = (serial_number: string) => {
    if (serial_number === editedController?.serial_number) {
      setEditedController(null);
      clearChannels();
    } else {
      const controller = controllers.find((controller) => controller.serial_number === serial_number);
      setEditedController(controller);

      const list = formik.values.list;
      const isControllerUsed = list.some((item) => item.serial_number === serial_number);

      if (!isControllerUsed) {
        list.push({
          id: controller.id,
          name: controller.name,
          serial_number: controller.serial_number,
          is_active: controller.is_active,
          thermocouples: [],
          channels: [],
        });

        formik.setFieldValue('list', list);
      }
    }
  };

  const availableControllersForModal = useMemo(() => {
    const usedSerialNumbers = new Set(formik.values.list.map((item) => item.serial_number));

    return controllers.filter((controller) => controller.is_active && !usedSerialNumbers.has(controller.serial_number));
  }, [controllers, formik.values.list]);

  const removeController = (serial_number: string) => {
    const updatedList = formik.values.list.filter((item) => item.serial_number !== serial_number);

    formik.setFieldValue('list', updatedList);

    if (editedController?.serial_number === serial_number) {
      setEditedController(null);
      clearChannels();
    }
  };

  const addControllers = (serialNumbers: string[]) => {
    if (serialNumbers.length === 0) {
      return;
    }

    const list = formik.values.list;
    let lastAddedController: Controller | null = null;

    serialNumbers.forEach((serial_number) => {
      const controller = controllers.find((item) => item.serial_number === serial_number);
      const alreadyUsed = list.some((item) => item.serial_number === serial_number);

      if (!controller || alreadyUsed) {
        return;
      }

      list.push({
        id: controller.id,
        name: controller.name,
        serial_number: controller.serial_number,
        is_active: controller.is_active,
        thermocouples: [],
        channels: [],
      });
      lastAddedController = controller;
    });

    formik.setFieldValue('list', list);

    if (lastAddedController) {
      setEditedController(lastAddedController);
    }

    setIsAddControllerModalOpen(false);
  };

  const selectAllOptions = (cycleEntityName: ComponentNameCycleList, options: ConfigurationCycleOption[]) => {
    const list = formik.values.list;

    if (cycleEntityName === ComponentNameCycleList.Controllers) {
      const updatedList = options.map((option) => {
        const controllerByOption = controllers.find((controller) => controller.serial_number === option.value);
        const usedItem = list.find((oldItem) => oldItem.serial_number === controllerByOption.serial_number);

        if (usedItem) {
          return usedItem;
        }

        return {
          id: controllerByOption.id,
          name: controllerByOption.name,
          serial_number: controllerByOption.serial_number,
          thermocouples: [],
          channels: [],
        };
      });

      formik.setFieldValue('list', updatedList);
      return;
    }

    if (
      cycleEntityName === ComponentNameCycleList.Channels ||
      cycleEntityName === ComponentNameCycleList.Thermocouples
    ) {
      const keyField = cycleEntityName.toLocaleLowerCase();
      const values =
        cycleEntityName === ComponentNameCycleList.Channels ? Object.keys(CHANNELS) : Object.keys(THERMOCOUPLES);

      const updatedList = list.map((item) => {
        item[keyField] = values;
        return item;
      });

      formik.setFieldValue('list', updatedList);
      return;
    }

    return;
  };

  const resetAllOptions = (cycleEntityName: ComponentNameCycleList) => {
    if (cycleEntityName === ComponentNameCycleList.Controllers) {
      setEditedController(null);
      clearChannels();
      formik.setFieldValue('list', []);
      return;
    }

    if (
      cycleEntityName === ComponentNameCycleList.Channels ||
      cycleEntityName === ComponentNameCycleList.Thermocouples
    ) {
      const list = formik.values.list;
      const keyField = cycleEntityName.toLocaleLowerCase();

      const updatedList = list.map((item) => {
        item[keyField] = [];
        return item;
      });

      formik.setFieldValue('list', updatedList);
      return;
    }
  };

  useEffect(() => {
    fetchControllersByOrganizationId(user.organization.id);

    return () => {
      clearChannels();
    };
  }, [user]);

  useEffect(() => {
    if (contractId) {
      getContractById(contractId);
    }
  }, [contractId]);

  useEffect(() => {
    fetchContractsByLocationId(clientId);
  }, [clientId]);

  useEffect(() => {
    getLocations();

    return () => {
      clearChannels();
    };
  }, []);

  useEffect(() => {
    if (editedController) {
      setLoadingChannels(true);
      getChannels(editedController.id)
        .then((channels) => {
          if (channels?.length) {
            setChannelsByController((prev) => ({ ...prev, [editedController.serial_number]: channels }));
          }
        })
        .finally(() => setLoadingChannels(false));
    }
  }, [editedController]);

  return (
    <>
      {!isDataLoaded && (
        <div className="flex h-full items-center justify-center">
          <SpinnerLoader />
        </div>
      )}

      {isDataLoaded && (
        <>
          <form className="p-add-cycle pb-4 font-medium md:pb-25" onSubmit={formik.handleSubmit}>
            <div className="rounded-sm border border-stroke bg-white p-8 shadow-default dark:border-strokedark dark:bg-boxdark">
              <div className="mb-4 flex justify-end">
                <InteractiveButton
                  size={ElementSize.LG}
                  text="Duplicate"
                  onClick={openModal}
                />
              </div>
              <div className="p-add-cycle__top-fields flex flex-col gap-y-1.5">
                <InputControl
                  type="text"
                  name="name"
                  label="Cycle name"
                  placeholder="Enter cycle name"
                  value={formik.values.name || ''}
                  customOnChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  showError={formik.touched.name && formik.errors.name ? true : false}
                  errorText={`${formik.errors.name}`}
                  customBlockClassName={ROW_BLOCK_CLASS}
                  customLabelClassName={ROW_LABEL_CLASS}
                />
                <SelectOption
                  name="locationId"
                  onChange={selectOption}
                  options={getLocationsOptions(locations)}
                  selectedOption={getSelectedOption(locations, formik.values.locationId)}
                  title="Client"
                  placeholder="Select your client"
                  showError={formik.touched.locationId && formik.errors.locationId ? true : false}
                  errorText={`${formik.errors.locationId}`}
                  customBlockClassName={ROW_BLOCK_CLASS}
                  customLabelClassName={ROW_LABEL_CLASS}
                />
                <SelectOption
                  disabled={disabledContract}
                  name="contractId"
                  onChange={selectOption}
                  options={getContractOptions(locations, contracts)}
                  selectedOption={getSelectedOption(contracts, formik.values.contractId)}
                  title="Contract"
                  placeholder="Select your contract"
                  showError={formik.touched.contractId && formik.errors.contractId ? true : false}
                  errorText={`${formik.errors.contractId}`}
                  customBlockClassName={ROW_BLOCK_CLASS}
                  customLabelClassName={ROW_LABEL_CLASS}
                />
              </div>
              {formik.values.contractId && (
                <div className="mb-2 flex w-full border-l-4 border-primary bg-primary bg-opacity-[8%] px-4 py-3 text-sm text-black dark:bg-opacity-20 dark:text-white">
                  <div className="mr-3 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white">
                    i
                  </div>
                  <p className="leading-relaxed">
                    Cycle recipe units are tied to the selected contract. This cycle is using{' '}
                    <span className="font-semibold">
                      {cycleSettingsMeasurement} ({CycleMeasurementSymbol[cycleSettingsMeasurement]})
                    </span>
                    . To use a different unit, update the contract unit.
                  </p>
                </div>
              )}
              <div className="p-add-cycle__divider h-1 py-6 after:block after:w-full after:border after:border-dashed after:border-stroke after:content-['']" />
              {hasMixedContractControllerUnits && (
                <div className="mb-2 flex w-full border-l-4 border-warning bg-warning bg-opacity-[10%] px-4 py-3 text-sm text-black dark:bg-opacity-20 dark:text-white">
                  <div className="mr-3 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-warning text-xs font-semibold text-white">
                    i
                  </div>
                  <p className="leading-relaxed">
                    Due to unit conversion and integer rounding, some displayed values may drift by plus or minus 1
                    degree after saving or reloading.
                  </p>
                </div>
              )}

              <div className="p-add-cycle__form flex flex-col gap-y-4">
                <div className="p-add-cycle__controller-tabs-row flex flex-wrap items-end justify-between gap-2">
                  <ControllerTabs
                    list={formik.values.list}
                    editingController={editedController?.serial_number ?? null}
                    onSelectTab={changeEditingController}
                    onRemoveTab={removeController}
                    disabled={disabledController}
                  />
                  <InteractiveButton
                    type={ButtonType.PrimaryInverse}
                    size={ElementSize.MD}
                    text="+ Controller"
                    onClick={() => setIsAddControllerModalOpen(true)}
                    disabled={disabledController}
                  />
                </div>
                <div className="p-add-cycle__configurations grid w-full items-stretch justify-between gap-x-4 gap-y-8 py-6 max-md:grid-cols-[100%] md:grid-cols-2">
                  {loadingChannels ? (
                    <div className="flex h-full w-full items-center justify-center">
                      <SpinnerLoader />
                    </div>
                  ) : (
                    <ConfigurationCycle
                      disabled={disableChannelsThermocouples}
                      editingController={editedController?.serial_number}
                      layout="tags"
                      listOptions={channelOptions}
                      onChange={(event) => handleSelect(ComponentNameCycleList.Channels, event)}
                      selectedValues={getEntityValue(ComponentNameCycleList.Channels)}
                      title={getTitleByEntity(ComponentNameCycleList.Channels)}
                      selectAllOptions={(data) => selectAllOptions(ComponentNameCycleList.Channels, data)}
                      resetAllOptions={() => resetAllOptions(ComponentNameCycleList.Channels)}
                    />
                  )}

                  <ConfigurationCycle
                    disabled={disableChannelsThermocouples}
                    editingController={editedController?.serial_number}
                    layout="tags"
                    listOptions={thermocoupleOptions}
                    onChange={(event) => handleSelect(ComponentNameCycleList.Thermocouples, event)}
                    selectedValues={getEntityValue(ComponentNameCycleList.Thermocouples)}
                    title={getTitleByEntity(ComponentNameCycleList.Thermocouples)}
                    selectAllOptions={(data) => selectAllOptions(ComponentNameCycleList.Thermocouples, data)}
                    resetAllOptions={() => resetAllOptions(ComponentNameCycleList.Thermocouples)}
                  />
                </div>

                <div className="flex min-h-6 items-center justify-start pb-2">
                  {formik.errors?.list?.length > 0 && formik.touched?.list ? (
                    <p className="text-sm text-red">{`${formik.errors?.list}`}</p>
                  ) : (
                    <p className="text-sm text-success">
                      You must select at least one channel or thermocouple for the selected controller
                    </p>
                  )}
                </div>
                <div className="p-add-cycle__cycles flex h-40 items-center overflow-auto rounded border border-stroke p-2 text-base text-black dark:text-white">
                  {!(formik.values?.list && formik.values?.list.length !== 0) ? (
                    <p className="w-full text-center text-2xl font-semibold">No controllers selected</p>
                  ) : (
                    <ul className="flex h-full w-full flex-auto flex-col">
                      {convertListToView(formik.values.list).map((item) => (
                        <li className="self-start pb-2" key={item}>
                          <p>{item}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <AddControllerModal
                  isVisible={isAddControllerModalOpen}
                  availableControllers={availableControllersForModal}
                  onClose={() => setIsAddControllerModalOpen(false)}
                  onAdd={addControllers}
                />

                {/* Cycle Settings Section */}
                <div className="p-add-cycle__cycle-settings mt-8">
                  <CycleSettingsInput
                    values={formik.values.cycleSettings}
                    onChange={(newSettings) => {
                      formik.setFieldValue('cycleSettings', newSettings);
                      // Trigger validation for cycle settings
                      setTimeout(() => formik.validateField('cycleSettings'), 0);
                    }}
                    onModeChange={selectOption}
                    onBlur={(field) => formik.setFieldTouched(field, true)}
                    errors={formik.errors.cycleSettings as any}
                    touched={formik.touched.cycleSettings as any}
                    contractTemperatureUnit={cycleSettingsMeasurement}
                    onRecipeSelect={async (recipeId: string) => {
                      // Fetch the selected recipe
                      const recipe = await getRecipe(recipeId);

                      // Handle different recipe types
                      if (recipe.mode === CycleSettings.PreHeat) {
                        const celsiusPreheatSettings = {
                          ...formik.values.cycleSettings,
                          mode: CycleSettings.PreHeat,
                          rate: recipe.rate,
                          finalTemp: recipe.finalTemp,
                          soakUpper: recipe.soakUpper,
                          soakLower: recipe.soakLower,
                          selectedRecipeId: recipe.id,
                        };
                        const preheatSettings = convertCycleSettingsUnits(
                          celsiusPreheatSettings,
                          CycleMeasurement.Celsius,
                          cycleSettingsMeasurement,
                        );

                        formik.setFieldValue('cycleSettings', preheatSettings);
                        formik.setFieldTouched('cycleSettings.rate', true);
                        formik.setFieldTouched('cycleSettings.finalTemp', true);
                        formik.setFieldTouched('cycleSettings.soakUpper', true);
                        formik.setFieldTouched('cycleSettings.soakLower', true);
                        setTimeout(() => formik.validateField('cycleSettings'), 0);
                      } else if (recipe.mode === CycleSettings.PostWeld) {
                        // Fetch recipe steps for PostWeld
                        const steps = await getRecipeSteps(recipeId);

                        const celsiusPostWeldSettings = {
                          ...formik.values.cycleSettings,
                          mode: CycleSettings.PostWeld,
                          name: recipe.name,
                          rateUpperTolerance: recipe.rateUpperTolerance,
                          rateLowerTolerance: recipe.rateLowerTolerance,
                          holdUpperTolerance: recipe.holdUpperTolerance,
                          holdLowerTolerance: recipe.holdLowerTolerance,
                          selectedRecipeId: recipe.id,
                          // Each step carries its own unit, independent of the
                          // recipe's top-level fields (which the backend always
                          // returns as plain Celsius) - getRecipeSteps reports it
                          // via step.isCelsius, same as the step's own edit page.
                          // Hardcoding isCelsius:true here regardless of that
                          // flag silently corrupted the stored rate/limit for any
                          // step actually recorded in Fahrenheit (doubly wrong:
                          // not converted to Celsius here, then re-labeled as
                          // already-Celsius again right before the cycle is
                          // created - see onSubmit above).
                          steps: steps.map((step: any, index: number) => {
                            const stepMeasurement = step.isCelsius
                              ? CycleMeasurement.Celsius
                              : CycleMeasurement.Fahrenheit;

                            return {
                              mode: step.mode,
                              durationDays: step.durationDays || 0,
                              durationHours: step.durationHours || 0,
                              durationMinutes: step.durationMinutes || 0,
                              heatingRate: convertTemperatureDelta(
                                step.heatingRate,
                                stepMeasurement,
                                CycleMeasurement.Celsius,
                              ) as number,
                              heatingLimit: convertAbsoluteTemperature(
                                step.heatingLimit,
                                stepMeasurement,
                                CycleMeasurement.Celsius,
                              ) as number,
                              order: index,
                              isCelsius: true,
                              units: CycleMeasurement.Celsius,
                            };
                          }),
                        };
                        const postWeldSettings = convertCycleSettingsUnits(
                          celsiusPostWeldSettings,
                          CycleMeasurement.Celsius,
                          cycleSettingsMeasurement,
                        );

                        formik.setFieldValue('cycleSettings', postWeldSettings);
                        formik.setFieldTouched('cycleSettings.name', true);
                        formik.setFieldTouched('cycleSettings.rateUpperTolerance', true);
                        formik.setFieldTouched('cycleSettings.rateLowerTolerance', true);
                        formik.setFieldTouched('cycleSettings.holdUpperTolerance', true);
                        formik.setFieldTouched('cycleSettings.holdLowerTolerance', true);
                        setTimeout(() => formik.validateField('cycleSettings'), 0);
                      }
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="h-28" aria-hidden="true" />
            <div className="c-add-cycle__sticky-footer fixed bottom-0 left-8 right-8 z-50 w-[calc(100%-4rem)] rounded-t-sm border-t border-stroke bg-white px-6 py-4 shadow-[0_-4px_10px_rgba(0,0,0,0.06)] dark:border-strokedark dark:bg-boxdark lg:left-[329px] lg:w-[calc(100%-23rem)]">
              <CycleConflictBanner conflict={cycleConflict} onDismiss={() => setCycleConflict(null)} />
              <div className="c-add-cycle__actions flex flex-row justify-end gap-x-4">
                <InteractiveButton
                  size={ElementSize.LG}
                  type={ButtonType.PrimaryInverse}
                  text="Cancel"
                  onClick={resetData}
                />
                <InteractiveButton
                  size={ElementSize.LG}
                  type={ButtonType.Primary}
                  text="Create"
                  disabled={disabledSubmit}
                  submit
                />
              </div>
            </div>
          </form>
        </>
      )}
      {isDuplicate && (
        <SearchCycle
          visibility={true}
          onClose={closeModal}
          getCycles={(query?: SearchCycleQuery) => getCycles({ query })}
          cyclesBySearch={cyclesBySearch}
          onCLickCycle={duplicateData}
          btnText="SELECT"
        />
      )}
    </>
  );
};

const mapState = (state: RootState) => ({
  controllers: state.controllers.controllers,
  locations: state.locations.locations,
  contracts: state.contracts.contracts,
  user: state.user.profile,
  cyclesBySearch: state.cycles.cycles,
  channels: state.channels.channels,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getControllersByOrganizationId: dispatch.controllers.getAllByOrganizationId,
  createCycle: dispatch.cycles.createCycle,
  duplicateCycle: dispatch.cycles.duplicateCycle,
  getLocations: dispatch.locations.getLocations,
  getContractsByLocationId: dispatch.contracts.getContractsByLocation,
  getContractById: dispatch.contracts.getContract,
  getCyclesByContractId: dispatch.cycles.getAllByContractId,
  getCyclesByLocationId: dispatch.cycles.getAllByLocationId,
  getChannels: dispatch.channels.getChannels,
  clearChannels: dispatch.channels.resetChannelState,
  getRecipe: dispatch.recipes.getRecipe,
  getRecipeSteps: dispatch.recipes.getRecipeSteps,
});

export default connect(mapState, mapDispatch)(AddCycle);
